import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getMyProfile, updateMyProfile, changeMyPassword, uploadMyAvatar,
} from '../utils/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { User, Mail, Phone, MapPin, KeyRound, Camera, Save, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { COUNTRIES } from '../utils/enrollmentOptions';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  superadmin: 'Super Administrador',
  admin: 'Administrador',
  professor: 'Profesor',
  student: 'Estudiante',
};

const Profile = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [uploading, setUploading] = useState(false);
  const avatarRef = useRef();

  const [profile, setProfile] = useState({
    name: '', email: '', phone: '', country: '', bio: '', avatar_url: '',
  });
  const [pwd, setPwd] = useState({ current_password: '', new_password: '', confirm: '' });

  useEffect(() => {
    if (authLoading) return;          // wait for AuthContext to finish hydrating
    if (!user) { navigate('/login'); return; }
    loadProfile();
  }, [user, authLoading, navigate]);

  const loadProfile = async () => {
    try {
      const r = await getMyProfile();
      setProfile({
        name: r.data.name || '',
        email: r.data.email || '',
        phone: r.data.phone || '',
        country: r.data.country || '',
        bio: r.data.bio || '',
        avatar_url: r.data.avatar_url || '',
      });
    } catch {
      toast.error('Error al cargar tu perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!profile.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      const payload = {
        name: profile.name,
        email: profile.email || null,
        phone: profile.phone || null,
        country: profile.country || null,
        bio: profile.bio || null,
      };
      const res = await updateMyProfile(payload);
      toast.success('Perfil actualizado');
      // Refresh local cache if user data changed
      if (res.data?.id) {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        const updated = { ...stored, name: res.data.name, email: res.data.email, avatar_url: res.data.avatar_url };
        localStorage.setItem('user', JSON.stringify(updated));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await uploadMyAvatar(fd);
      setProfile(p => ({ ...p, avatar_url: res.data.url }));
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...stored, avatar_url: res.data.url }));
      toast.success('Foto actualizada');
    } catch {
      toast.error('Error al subir la foto');
    } finally {
      setUploading(false);
      if (avatarRef.current) avatarRef.current.value = '';
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 6) { toast.error('La nueva contraseña debe tener al menos 6 caracteres'); return; }
    if (pwd.new_password !== pwd.confirm) { toast.error('Las contraseñas nuevas no coinciden'); return; }
    setSavingPwd(true);
    try {
      await changeMyPassword({
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success('Contraseña actualizada');
      setPwd({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setSavingPwd(false);
    }
  };

  if (authLoading || loading) return <div className="min-h-screen flex items-center justify-center">Cargando perfil...</div>;

  const avatarSrc = profile.avatar_url
    ? (profile.avatar_url.startsWith('/api') ? `${API_BASE}${profile.avatar_url}` : profile.avatar_url)
    : null;

  return (
    <div className="py-12 bg-secondary min-h-screen" data-testid="profile-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-center gap-5 mb-8 flex-wrap">
          <div className="relative">
            {avatarSrc ? (
              <img src={avatarSrc} alt={profile.name} className="w-24 h-24 rounded-full object-cover ring-4 ring-accent/30" data-testid="profile-avatar-img" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-accent/10 flex items-center justify-center ring-4 ring-accent/30">
                <User className="w-10 h-10 text-accent" />
              </div>
            )}
            <button
              onClick={() => avatarRef.current?.click()}
              type="button"
              className="absolute bottom-0 right-0 bg-primary text-primary-foreground p-2 rounded-full hover:bg-primary/90 transition-colors"
              data-testid="profile-upload-avatar-btn"
              title="Cambiar foto"
            >
              <Camera className="w-4 h-4" />
            </button>
            <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarPick} />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl font-heading font-bold text-primary truncate">{profile.name || user?.name}</h1>
            <p className="text-sm text-muted-foreground">{profile.email}</p>
            <div className="mt-1.5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold">
              <ShieldCheck className="w-3.5 h-3.5" />
              {ROLE_LABELS[user?.role] || user?.role}
            </div>
            {uploading && <p className="text-xs text-blue-600 mt-1">Subiendo foto...</p>}
          </div>
        </div>

        <Tabs defaultValue="info">
          <TabsList className="mb-4">
            <TabsTrigger value="info" data-testid="tab-info">Información</TabsTrigger>
            <TabsTrigger value="password" data-testid="tab-password">Contraseña</TabsTrigger>
          </TabsList>

          {/* INFO */}
          <TabsContent value="info">
            <Card>
              <CardContent className="p-6">
                <form onSubmit={handleSaveProfile} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" />Nombre completo *</Label>
                      <Input value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
                        required data-testid="profile-name-input" />
                    </div>
                    <div>
                      <Label className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />Email</Label>
                      <Input type="email" value={profile.email} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))}
                        data-testid="profile-email-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />Teléfono</Label>
                      <Input value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
                        placeholder="+506 8888 8888" data-testid="profile-phone-input" />
                    </div>
                    <div>
                      <Label className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />País</Label>
                      <Select value={profile.country} onValueChange={v => setProfile(p => ({ ...p, country: v }))}>
                        <SelectTrigger data-testid="profile-country-select"><SelectValue placeholder="Selecciona país..." /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Biografía / Acerca de mí</Label>
                    <Textarea value={profile.bio} onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))}
                      rows={4} placeholder="Cuéntanos un poco sobre ti, tu experiencia y áreas de interés..." />
                  </div>
                  <div className="pt-3 border-t">
                    <Button type="submit" disabled={saving} className="gap-2" data-testid="profile-save-btn">
                      <Save className="w-4 h-4" />
                      {saving ? 'Guardando...' : 'Guardar cambios'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PASSWORD */}
          <TabsContent value="password">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-3 p-3 rounded-xl bg-blue-50 border border-blue-200 mb-5 text-sm text-blue-800">
                  <KeyRound className="w-4 h-4 mt-0.5 shrink-0" />
                  <p>Te recomendamos usar una contraseña única de al menos 8 caracteres con letras, números y símbolos.</p>
                </div>
                <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                  <div>
                    <Label>Contraseña actual *</Label>
                    <Input type="password" value={pwd.current_password}
                      onChange={e => setPwd(p => ({ ...p, current_password: e.target.value }))}
                      required data-testid="current-password-input" />
                  </div>
                  <div>
                    <Label>Nueva contraseña *</Label>
                    <Input type="password" value={pwd.new_password}
                      onChange={e => setPwd(p => ({ ...p, new_password: e.target.value }))}
                      required minLength={6} data-testid="new-password-input"
                      placeholder="Mínimo 6 caracteres" />
                  </div>
                  <div>
                    <Label>Confirmar nueva contraseña *</Label>
                    <Input type="password" value={pwd.confirm}
                      onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))}
                      required data-testid="confirm-password-input" />
                  </div>
                  <Button type="submit" disabled={savingPwd} className="gap-2" data-testid="change-password-btn">
                    <KeyRound className="w-4 h-4" />
                    {savingPwd ? 'Actualizando...' : 'Cambiar contraseña'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Profile;
